//? React
import { useEffect, useState } from "react";

//? External
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { FaTrashAlt, FaPen, FaArrowLeft, FaListUl } from "react-icons/fa";

//* Data hook + API + types
import { useQuestionLists } from "../../hooks/questionLists/useQuestionLists";
import { questionListsAPI } from "../../hooks/questionLists/questionListsAPI";
import { type QuestionListDetail } from "../../hooks/questionLists/types/types";

interface MyListsModalProps {
  show: boolean;
  onHide: () => void;
  token: string;
}

/**
 * Lightweight management modal for the user's named lists: list overview with
 * rename/delete, plus a detail view to inspect and remove questions from a list.
 */
export default function MyListsModal({ show, onHide, token }: MyListsModalProps) {
  const { lists, loading, renameList, deleteList, removeItem } =
    useQuestionLists(token);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  // openList === null mostra a visão geral; preenchido mostra as questões da lista.
  const [openList, setOpenList] = useState<QuestionListDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [removingQuestionId, setRemovingQuestionId] = useState<string | null>(null);

  useEffect(() => {
    if (!show) {
      setOpenList(null);
      setEditingId(null);
    }
  }, [show]);

  const refreshOpenList = async (listId: string) => {
    setLoadingDetail(true);
    try {
      const detail = await questionListsAPI.getById(listId, token);
      setOpenList(detail);
    } catch (error) {
      console.error("Erro ao carregar a lista:", error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleRename = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    try {
      await renameList({ id, name });
      setEditingId(null);
    } catch (error) {
      console.error("Erro ao renomear a lista:", error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteList(id);
    } catch (error) {
      console.error("Erro ao excluir a lista:", error);
    }
  };

  const handleRemoveQuestion = async (questionId: string) => {
    if (!openList) return;
    setRemovingQuestionId(questionId);
    try {
      await removeItem({ listId: openList.id, questionId });
      await refreshOpenList(openList.id);
    } catch (error) {
      console.error("Erro ao remover a questão da lista:", error);
    } finally {
      setRemovingQuestionId(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {openList ? (
            <Button
              id="btn-back-to-lists"
              type="button"
              variant="link"
              className="p-0 me-2 text-decoration-none"
              aria-label="Voltar para as listas"
              onClick={() => setOpenList(null)}
            >
              <FaArrowLeft />
            </Button>
          ) : (
            <FaListUl className="me-2" />
          )}
          {openList ? openList.name : "Minhas listas"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex flex-column gap-2">
        {openList ? (
          loadingDetail ? (
            <div className="d-flex justify-content-center py-3">
              <Spinner animation="border" role="status" />
            </div>
          ) : openList.questions.length === 0 ? (
            <p className="text-muted mb-0">Esta lista está vazia.</p>
          ) : (
            <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
              {openList.questions.map((question) => (
                <li
                  key={question.id}
                  className="d-flex justify-content-between align-items-center border rounded p-2"
                >
                  <div className="d-flex flex-column">
                    <strong>{question.displayLabel ?? "Questão"}</strong>
                    <small className="text-muted">
                      {question.discipline?.name ?? "-"}
                    </small>
                  </div>
                  <Button
                    id={`btn-remove-list-question-${question.id}`}
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    aria-label="Remover questão da lista"
                    disabled={removingQuestionId === question.id}
                    onClick={() => handleRemoveQuestion(question.id)}
                  >
                    <FaTrashAlt />
                  </Button>
                </li>
              ))}
            </ul>
          )
        ) : loading ? (
          <div className="d-flex justify-content-center py-3">
            <Spinner animation="border" role="status" />
          </div>
        ) : lists.length === 0 ? (
          <p className="text-muted mb-0">Você ainda não tem listas.</p>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {lists.map((list) => (
              <li
                key={list.id}
                className="d-flex justify-content-between align-items-center border rounded p-2 gap-2"
              >
                {editingId === list.id ? (
                  <Form.Control
                    type="text"
                    value={editName}
                    autoFocus
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleRename(list.id);
                      }
                    }}
                  />
                ) : (
                  <button
                    id={`btn-open-list-${list.id}`}
                    type="button"
                    className="btn btn-link p-0 text-decoration-none text-start flex-grow-1"
                    onClick={() => refreshOpenList(list.id)}
                  >
                    {list.name}{" "}
                    <small className="text-muted">({list.itemsCount})</small>
                  </button>
                )}

                <div className="d-flex gap-1">
                  {editingId === list.id ? (
                    <Button
                      id={`btn-save-rename-${list.id}`}
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => handleRename(list.id)}
                    >
                      Salvar
                    </Button>
                  ) : (
                    <Button
                      id={`btn-rename-list-${list.id}`}
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      aria-label="Renomear lista"
                      onClick={() => {
                        setEditingId(list.id);
                        setEditName(list.name);
                      }}
                    >
                      <FaPen />
                    </Button>
                  )}
                  <Button
                    id={`btn-delete-list-${list.id}`}
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    aria-label="Excluir lista"
                    onClick={() => handleDelete(list.id)}
                  >
                    <FaTrashAlt />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button id="btn-close-my-lists" type="button" variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
